/**
 * Revoke Technician Access confirmation dialog.
 * Admin-only. Clears all extension attributes from the Entra user.
 */

import { useCallback, useEffect, useRef } from "react";
import { X, ShieldX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/sc/glass-card";
import { useRevokeTechnicianAccess } from "@/hooks/use-technicians";
import type { TechnicianSummary } from "@/types";

interface RevokeAccessDialogProps {
  readonly open: boolean;
  readonly technician: TechnicianSummary | null;
  readonly onClose: () => void;
}

export function RevokeAccessDialog({
  open,
  technician,
  onClose,
}: RevokeAccessDialogProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const revokeMutation = useRevokeTechnicianAccess();

  const handleClose = useCallback(() => {
    revokeMutation.reset();
    onClose();
  }, [onClose, revokeMutation]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleClose]);

  const handleRevoke = useCallback(async () => {
    if (!technician) return;

    try {
      await revokeMutation.mutateAsync(technician.userId);
      handleClose();
    } catch {
      // Error state handled by revokeMutation.isError
    }
  }, [technician, revokeMutation, handleClose]);

  if (!open || !technician) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === backdropRef.current) handleClose();
      }}
    >
      <GlassCard className="w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldX size={20} className="text-sc-danger" />
            <h2 className="text-h3 font-display text-sc-navy">
              Revoke Access
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-btn text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Warning */}
        <div className="mb-4 p-4 rounded-input bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <p className="text-body-sm text-sc-danger font-medium mb-1">
            This action will remove all technician attributes from:
          </p>
          <p className="text-body-sm text-sc-text font-medium">
            {technician.displayName}
          </p>
          <p className="text-label text-sc-text-muted">
            {technician.userPrincipalName}
          </p>
          <p className="text-label text-sc-text-muted mt-2">
            The user will lose all technician access to customer tenants. This
            can be reversed by re-adding them as a technician.
          </p>
        </div>

        {revokeMutation.isError && (
          <p className="text-label text-sc-danger mb-3">
            {(revokeMutation.error as Error).message ?? "Failed to revoke access"}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRevoke}
            disabled={revokeMutation.isPending}
          >
            {revokeMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Revoking...
              </>
            ) : (
              "Revoke Access"
            )}
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}
